import React, { useState, useEffect, useRef } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { RedeemCoupon, PlayCheckinSound, formatMexicoDate } from './../../../../utilities/BL/OptimoOpticas';

/**
 * Modal de alta visibilidad (Light Theme de Alto Contraste) para la validación y canje
 * presencial de cupones en sucursales u operarios de estadio.
 * Incorpora emulador Keyboard Wedge con buffer ultrarrápido (< 120ms) para pistolas
 * lectoras USB / Bluetooth y sintetizador acústico Web Audio API.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onCheckinSuccess] - Callback opcional tras un canje exitoso.
 * @param {number} [props.initialRedeemed=0] - Cantidad inicial de cupones canjeados.
 */
export function OptimoOpticasCheckinModal({
    isOpen,
    onClose,
    onCheckinSuccess,
    initialRedeemed = 0
}) {
    const { showMessage } = useFloatMessage();

    // Estados de escaneo y procesamiento
    const [manualInput, setManualInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [sessionRedeemedCount, setSessionRedeemedCount] = useState(0);
    const [totalRedeemedCount, setTotalRedeemedCount] = useState(initialRedeemed);

    // Estado de Mute persistente en localStorage con referencia mutable contra Stale Closure
    const [isMuted, setIsMuted] = useState(() => {
        try {
            return localStorage.getItem('optimoopticas_checkin_muted') === 'true';
        } catch {
            return false;
        }
    });
    const isMutedRef = useRef(isMuted);

    // Estado del resultado más reciente
    // { status: 'GRANTED' | 'ALREADY_REDEEMED' | 'DISQUALIFIED' | 'INVALID' | 'ERROR', message, couponCode, name, email, phone, winnerRank, redeemedAtUtc, redeemedBy, disqualifiedReason }
    const [scanResult, setScanResult] = useState(null);

    // Buffers de captura para pistola lectora (Keyboard Wedge)
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const isManualFocusedRef = useRef(false);

    useEffect(() => {
        setTotalRedeemedCount(initialRedeemed);
    }, [initialRedeemed]);

    // Manejador del toggle de audio con persistencia inmediata
    const handleToggleMute = () => {
        setIsMuted(prev => {
            const next = !prev;
            isMutedRef.current = next;
            try {
                localStorage.setItem('optimoopticas_checkin_muted', next ? 'true' : 'false');
            } catch (e) {
                console.warn("No se pudo persistir estado mute en localStorage:", e);
            }
            return next;
        });
    };

    // Escucha global de eventos de teclado para pistola óptica / escáner de códigos
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            // Si el foco está en el input manual, dejar que el usuario escriba libremente
            if (isManualFocusedRef.current) return;

            const now = Date.now();
            const timeDiff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            if (e.key === 'Enter') {
                if (scanBufferRef.current.length >= 3) {
                    const scanned = scanBufferRef.current.replace(/[\r\n\t]+/g, '').trim();
                    scanBufferRef.current = '';
                    processCheckin(scanned);
                } else {
                    scanBufferRef.current = '';
                }
                return;
            }

            // Normal typing humano (> 120ms) vs pistola lectora ultrarrápida (< 60ms)
            if (timeDiff > 120 && scanBufferRef.current.length > 0) {
                scanBufferRef.current = '';
            }

            if (e.key.length === 1) {
                scanBufferRef.current += e.key;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    /**
     * Extrae un folio limpio legible (ej. #OPTI-44NG2R) a partir de un token criptográfico o entrada bruta.
     */
    const getCleanDisplayCode = (rawCode, fallback) => {
        let target = (rawCode && String(rawCode).trim().length > 0) ? String(rawCode).trim() : (fallback ? String(fallback).trim() : '');
        if (!target.includes('.') && target.length > 20) {
            try {
                let base64 = target.replace(/-/g, '+').replace(/_/g, '/');
                while (base64.length % 4) base64 += '=';
                const decoded = atob(base64);
                if (decoded && decoded.includes('.')) {
                    target = decoded;
                }
            } catch (e) {
                // No es Base64, mantener target original
            }
        }
        if (target.includes('.')) {
            const parts = target.split('.');
            if (parts.length >= 2 && parts[1]) {
                return parts[1];
            }
        }
        return target;
    };

    /**
     * Valida y procesa el canje del cupón a través de la capa BL.
     */
    const processCheckin = async (scanData) => {
        if (!scanData || isProcessing) return;
        const cleanInput = String(scanData).replace(/[\r\n\t]+/g, '').trim();
        if (!cleanInput) return;

        setIsProcessing(true);
        try {
            const res = await RedeemCoupon(cleanInput);
            const displayCode = getCleanDisplayCode(res?.couponCode, cleanInput);

            if (res && res.success && !res.alreadyRedeemed && res.status !== 'ALREADY_REDEEMED' && res.status !== 'ALREADY_ATTENDED') {
                // ÉXITO: Canje Concedido
                if (!isMutedRef.current) PlayCheckinSound('success');
                setSessionRedeemedCount(prev => prev + 1);
                setTotalRedeemedCount(prev => prev + 1);

                setScanResult({
                    status: 'GRANTED',
                    message: res.message || '¡Canje Aprobado! El cupón es válido para su redención.',
                    couponCode: displayCode,
                    name: res.name || res.participantName || 'Aficionado Registrado',
                    email: res.email || null,
                    phone: res.phone || null,
                    winnerRank: res.winnerRank || null,
                    discountDescription: res.discountDescription || 'Descuento especial en Optimo Ópticas',
                    redeemedAtUtc: res.redeemedAtUtc || new Date().toISOString()
                });

                if (onCheckinSuccess) onCheckinSuccess(res);
            } else if (res && (res.alreadyRedeemed || res.status === 'ALREADY_REDEEMED' || res.status === 'ALREADY_ATTENDED')) {
                // ADVERTENCIA: Ya Canjeado Previamente
                if (!isMutedRef.current) PlayCheckinSound('warning');
                setScanResult({
                    status: 'ALREADY_REDEEMED',
                    message: res.message || '⚠️ Este cupón ya ha sido canjeado anteriormente.',
                    couponCode: displayCode,
                    name: res.name || res.participantName || 'Aficionado',
                    redeemedAtUtc: res.redeemedAtUtc || null,
                    redeemedBy: res.redeemedBy || res.operator || 'Operador en Sucursal'
                });
            } else if (res && (res.isDisqualified || res.status === 'DISQUALIFIED')) {
                // ERROR: Descalificado
                if (!isMutedRef.current) PlayCheckinSound('error');
                setScanResult({
                    status: 'DISQUALIFIED',
                    message: res.message || '❌ Acceso Denegado. El participante o cupón se encuentra descalificado.',
                    couponCode: displayCode,
                    name: res.name || 'Aficionado',
                    disqualifiedReason: res.disqualifiedReason || res.reason || 'Sin motivo especificado'
                });
            } else {
                // ERROR: Inválido o inexistente
                if (!isMutedRef.current) PlayCheckinSound('error');
                setScanResult({
                    status: 'INVALID',
                    message: res?.message || 'No se encontró un cupón de Optimo Ópticas válido con este folio o token.',
                    couponCode: displayCode
                });
            }
        } catch (error) {
            console.error("Error al procesar check-in de Optimo Ópticas:", error);
            if (!isMutedRef.current) PlayCheckinSound('error');
            setScanResult({
                status: 'ERROR',
                message: 'Error de conexión con el servidor al verificar el cupón. Por favor reintenta.'
            });
        } finally {
            setIsProcessing(false);
            setManualInput('');
        }
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualInput.trim()) {
            processCheckin(manualInput.trim());
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(6px)', zIndex: 1080 }}
            onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) onClose(); }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 bg-white text-dark overflow-hidden">
                    {/* Header de Alto Contraste */}
                    <div className="modal-header border-bottom px-4 py-3 bg-white">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="bg-danger text-white rounded-3 p-2 d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                                <i className="bi bi-upc-scan fs-4"></i>
                            </div>
                            <div>
                                <h5 className="modal-title fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <span>Módulo de Canje Staff</span>
                                    <span className="badge bg-danger text-white fs-6 px-2.5 py-1">
                                        Optimo Ópticas
                                    </span>
                                </h5>
                                <div className="text-secondary small">
                                    Validación de cupones digitales en sucursal o estadio
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2.5 ms-auto flex-wrap">
                            <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fs-6 fw-bold">
                                Canjeados en Sesión: {sessionRedeemedCount}
                            </span>
                            <button 
                                type="button" 
                                className={`btn btn-sm ${isMuted ? 'btn-outline-danger' : 'btn-outline-secondary'} d-flex align-items-center gap-1.5 px-3 py-1.5 fw-semibold`}
                                onClick={handleToggleMute}
                                title={isMuted ? "Activar sonido de confirmación" : "Silenciar feedback sonoro"}
                            >
                                <i className={`bi ${isMuted ? 'bi-volume-mute-fill text-danger fs-6' : 'bi-volume-up-fill text-success fs-6'}`}></i>
                                <span className="small">{isMuted ? 'Silenciado' : 'Sonido Activo'}</span>
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                aria-label="Close"
                                disabled={isProcessing}
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body p-4 bg-light">
                        <div className="row g-4">
                            {/* Columna Izquierda: Entrada Manual y Controles de Pistola */}
                            <div className="col-12 col-lg-5">
                                <div className="card border-0 bg-white p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="bi bi-qr-code text-danger fs-4"></i>
                                            <h6 className="fw-bold text-dark mb-0 fs-5">
                                                Lectura Óptica o Entrada Manual
                                            </h6>
                                        </div>
                                        <p className="text-secondary small mb-3">
                                            Apunta la pistola lectora láser/QR al cupón del aficionado o escribe el folio o token de verificación:
                                        </p>

                                        <form onSubmit={handleManualSubmit}>
                                            <div className="input-group mb-2">
                                                <input 
                                                    type="text" 
                                                    className="form-control bg-light text-dark border-secondary-subtle font-monospace fs-5 py-2.5"
                                                    placeholder="Folio (#OPTI-...), Token, Celular o Correo..."
                                                    value={manualInput}
                                                    onChange={(e) => setManualInput(e.target.value)}
                                                    onFocus={() => { isManualFocusedRef.current = true; }}
                                                    onBlur={() => { isManualFocusedRef.current = false; }}
                                                    disabled={isProcessing}
                                                    autoFocus
                                                />
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-danger fw-bold px-3.5 d-flex align-items-center gap-1.5"
                                                    disabled={isProcessing || !manualInput.trim()}
                                                >
                                                    {isProcessing ? (
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-arrow-return-left"></i>
                                                            <span>Canjear</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>

                                        {isProcessing && (
                                            <div className="alert alert-warning py-2.5 px-3 rounded-3 small fw-bold d-flex align-items-center gap-2 mb-3 animate__animated animate__fadeIn">
                                                <div className="spinner-border spinner-border-sm text-warning" role="status"></div>
                                                <span>⏳ Validando cupón y registrando canje con el servidor...</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Nota de Modo Continuo */}
                                    <div className="bg-light p-3 rounded-3 border border-secondary-subtle mt-3">
                                        <div className="d-flex align-items-start gap-2.5">
                                            <i className="bi bi-broadcast text-danger fs-5 mt-0.5"></i>
                                            <div className="small text-secondary" style={{ fontSize: '0.82rem', lineHeight: '1.45' }}>
                                                <strong className="text-dark">Modo Pistola Continuo Activo:</strong> El escáner USB / Bluetooth procesa lecturas al instante sin requerir que el cursor esté dentro del campo de texto.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Columna Derecha: Tarjeta de Feedback Visual Grande de Alto Contraste */}
                            <div className="col-12 col-lg-7">
                                {isProcessing ? (
                                    <div className="card border-0 bg-white text-center p-5 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                                        <div className="spinner-grow text-danger mb-3" style={{ width: '4rem', height: '4rem' }} role="status"></div>
                                        <h4 className="fw-bold text-danger mb-1">Validando cupón...</h4>
                                        <p className="text-secondary small mb-0">Consultando base de datos segura y verificando autenticidad HMAC...</p>
                                    </div>
                                ) : scanResult?.status === 'GRANTED' ? (
                                    /* TARJETA VERDE: CANJE CONCEDIDO */
                                    <div className="card border-2 border-success bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__zoomIn">
                                        <div className="bg-success text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '85px', height: '85px' }}>
                                            <i className="bi bi-check-lg" style={{ fontSize: '3.2rem' }}></i>
                                        </div>
                                        <span className="badge bg-success text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ✅ CANJE CONCEDIDO
                                        </span>
                                        <h2 className="fw-bold text-dark text-uppercase mb-1 mt-1 fs-3">
                                            {scanResult.name}
                                        </h2>
                                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
                                            <span className="badge bg-dark text-warning fs-5 font-monospace px-3 py-1.5 fw-bold">
                                                {scanResult.couponCode}
                                            </span>
                                            {scanResult.winnerRank && (
                                                <span className="badge bg-warning text-dark fs-6 px-3 py-1.5 fw-bold">
                                                    🏆 Ganador #{scanResult.winnerRank}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-success fw-bold mb-2 fs-6">
                                            {scanResult.discountDescription}
                                        </p>
                                        <div className="text-muted small">
                                            <i className="bi bi-clock-history me-1"></i> Registrado: {formatMexicoDate(scanResult.redeemedAtUtc)}
                                        </div>
                                    </div>
                                ) : scanResult?.status === 'ALREADY_REDEEMED' ? (
                                    /* TARJETA AMARILLA: YA CANJEADO PREVIAMENTE */
                                    <div className="card border-2 border-warning bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__shakeX">
                                        <div className="bg-warning text-dark rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '85px', height: '85px' }}>
                                            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '3rem' }}></i>
                                        </div>
                                        <span className="badge bg-warning text-dark fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ⚠️ YA CANJEADO PREVIAMENTE
                                        </span>
                                        <h3 className="fw-bold text-dark text-uppercase mb-1 mt-1 fs-4">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-warning fs-5 font-monospace px-3 py-1.5 fw-bold mb-3">
                                            {scanResult.couponCode}
                                        </span>
                                        <div className="alert alert-warning border border-warning text-dark fw-bold px-4 py-2 rounded-3 mb-2">
                                            <div>Canjeado el: {formatMexicoDate(scanResult.redeemedAtUtc)}</div>
                                            {scanResult.redeemedBy && (
                                                <div className="small fw-semibold mt-0.5">Operador: {scanResult.redeemedBy}</div>
                                            )}
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            Este cupón ya fue marcado como canjeado y no puede redimirse nuevamente.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'DISQUALIFIED' ? (
                                    /* TARJETA ROJA: DESCALIFICADO */
                                    <div className="card border-2 border-danger bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__headShake">
                                        <div className="bg-danger text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '85px', height: '85px' }}>
                                            <i className="bi bi-shield-x" style={{ fontSize: '3rem' }}></i>
                                        </div>
                                        <span className="badge bg-danger text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ❌ CUPÓN DESCALIFICADO
                                        </span>
                                        <h3 className="fw-bold text-dark text-uppercase mb-1 mt-1 fs-4">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-danger fs-5 font-monospace px-3 py-1.5 fw-bold mb-3">
                                            {scanResult.couponCode}
                                        </span>
                                        <div className="alert alert-danger border-0 bg-danger text-white fw-bold px-4 py-2 rounded-3 mb-2">
                                            Motivo: {scanResult.disqualifiedReason}
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            Cupón revocado por el administrador de la dinámica.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'INVALID' || scanResult?.status === 'ERROR' ? (
                                    /* TARJETA ROJA: INVÁLIDO O ERROR */
                                    <div className="card border-2 border-danger-subtle bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-x-circle text-danger mb-3" style={{ fontSize: '3.8rem' }}></i>
                                        <h4 className="fw-bold text-danger mb-2">Cupón No Válido</h4>
                                        <p className="text-dark fs-6 mb-2">{scanResult.message}</p>
                                        {scanResult.couponCode && (
                                            <span className="badge bg-light text-secondary font-monospace border mb-3">
                                                Folio consultado: {scanResult.couponCode}
                                            </span>
                                        )}
                                        <span className="text-muted small">
                                            Verifica que el aficionado muestre su tarjeta digital oficial de Optimo Ópticas.
                                        </span>
                                    </div>
                                ) : (
                                    /* ESTADO INICIAL EN ESPERA */
                                    <div className="card border-2 border-dashed border-secondary-subtle bg-white text-center p-5 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-eyeglasses text-danger mb-3" style={{ fontSize: '4.5rem', opacity: 0.85 }}></i>
                                        <h3 className="fw-bold text-dark mb-2">Listo para Validar</h3>
                                        <p className="text-secondary fs-6 mb-3">
                                            Apunta la pistola lectora láser al código QR o introduce manualmente el folio del cupón.
                                        </p>
                                        <div className="d-flex align-items-center gap-2 text-danger small bg-danger-subtle px-3 py-2 rounded-pill border border-danger border-opacity-25 fw-semibold">
                                            <i className="bi bi-broadcast"></i>
                                            <span>Esperando lectura óptica o folio manual...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OptimoOpticasCheckinModal;
